import React, { useEffect, useState, useCallback } from "react";
import { Link, useLocation } from 'react-router-dom';
import { Row, Col, Button, Alert } from 'react-bootstrap';
import axios from "axios";
import Layout from '../../components/layouts/Main';
import config from '../../config';
import { toast } from 'react-toastify';
import { PulseLoader } from "react-spinners";
import PackagingMaterialPOTable from "../../components/packagingMaterial/PO/PackagingMaterialPOTable";
import PermissionGate from "../../components/common/PermissionGate";
import { useHasPermission, PERMISSIONS } from "../../utils/permissions";

export default function PackagingMaterialPOList() {
  const location = useLocation();
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Permission checks for Packing Materials
  const canViewPMPO = useHasPermission(PERMISSIONS.PM_PO_VIEW);
  const canCreatePMPO = useHasPermission(PERMISSIONS.PM_PO_CREATE);
  const canEditPMPO = useHasPermission(PERMISSIONS.PM_PO_EDIT);
  const canDeletePMPO = useHasPermission(PERMISSIONS.PM_PO_DELETE);

  useEffect(() => {
    if (location.state?.toast) {
      toast.success(location.state.toast);
    }
  }, [location]);

  // Fetch PM purchase orders
  const fetchOrders = useCallback(async () => {
    if (!canViewPMPO) {
      toast.error("You don't have permission to view packaging material purchase orders");
      return;
    }

    setIsLoading(true);
    try {
      const res = await axios.get(`${config.API_BASE_URL}/pm-purchase-orders`);
      setOrders(res.data);
      if (sessionStorage.getItem('requestSuccess') === 'true') {
        toast.success('Request added successfully!');
        sessionStorage.removeItem('requestSuccess');
      }
    } catch (err) {
      console.error("Error fetching packaging material purchase orders", err);
      toast.error("Failed to fetch packaging material purchase orders");
    } finally {
      setIsLoading(false);
    }
  }, [canViewPMPO]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  // If user can't view PM PO, show permission denied
  if (!canViewPMPO) {
    return (
      <Layout>
        <Alert variant="danger" className="d-flex align-items-center">
          <i className="fas fa-exclamation-triangle me-2"></i>
          <div>
            <strong>Access Denied</strong>
            <p className="mb-0">You don't have permission to view packing material purchase orders. Contact your administrator for access.</p>
          </div>
        </Alert>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Breadcrumb */}
      <nav aria-label="breadcrumb" className="mb-3">
        <ol className="breadcrumb">
          <li className="breadcrumb-item"><Link to="/">Dashboard</Link></li>
          <li className="breadcrumb-item"><Link to="/purchase-orders">Purchase Orders</Link></li>
          <li className="breadcrumb-item active">Packaging Material POs</li>
        </ol>
      </nav>

      {/* PM Purchase Orders Header */}
      <Row className="mb-4 gy-3">
        <Col className="col">
          <h2>Packing Material Purchase Orders</h2>
          <small className="text-muted">
            {canEditPMPO ? "View and manage" : "View"} packing material purchase orders
            {!canCreatePMPO && !canEditPMPO && !canDeletePMPO && " (read-only)"}
          </small>
        </Col>
        <Col className="text-end col-auto">
          <div className="d-flex justify-content-end flex-wrap gap-2">
            <PermissionGate
              require={PERMISSIONS.PM_PO_CREATE}
              fallback={
                <Button variant="outline-primary" disabled title="You don't have permission to create packing material purchase orders">
                  <i className="fas fa-plus me-2" /> New Packing Material PO
                </Button>
              }
            >
              <Link to="/new-packaging-material-po" className="btn btn-primary">
                <i className="fas fa-plus me-2" /> New Packing Material PO
              </Link>
            </PermissionGate>
          </div>
        </Col>
      </Row>

      {isLoading ? (
        <div className="text-center py-4">
          <PulseLoader color="#014b7cbb" size={9} />
        </div>
      ) : (
        <PackagingMaterialPOTable 
          orders={orders} 
          canEdit={canEditPMPO}
          canDelete={canDeletePMPO}
          canView={canViewPMPO}
        />
      )}
    </Layout>
  );
}
